<template>
    <v-container fluid class="pa-0">
        <v-snackbar
            v-model="snackbar.isVisible"
            :color="snackbar.color"
            :timeout="snackbar.timeout"
        >
            {{ snackbar.text }}
        </v-snackbar>

        <!-- Configuration & AI Modification - Top Section -->
        <v-row>
            <v-col cols="12">
                <v-card elevation="2">
                    <v-card-title class="primary white--text">
                        <v-icon left color="white">mdi-cog</v-icon>
                        Configuración y Personalización
                    </v-card-title>
                    <v-card-text class="pt-4">
                        <v-row>
                            <!-- Logo and Footers -->
                            <v-col cols="12" md="4">
                                <h3 class="mb-3 text-subtitle-1 font-weight-bold">
                                    <v-icon small left>mdi-image</v-icon>
                                    Personalización de Imagen
                                </h3>
                                <v-file-input
                                    label="Logo"
                                    v-model="logoFile"
                                    outlined
                                    dense
                                    prepend-icon=""
                                    accept=".jpg, .png, .jpeg"
                                    class="file-input--outlined rounded-lg"
                                    hide-details="auto"
                                    @change="onLogoChange"
                                >
                                    <template #prepend-inner>
                                        <v-icon color="primary" left>
                                            mdi-paperclip
                                        </v-icon>
                                    </template>
                                </v-file-input>

                                <v-img
                                    v-if="configuration && configuration.path_url"
                                    :src="configuration.path_url"
                                    class="mt-3 mb-3"
                                    max-height="100"
                                    contain
                                ></v-img>

                                <v-textarea
                                    v-model="localFooter"
                                    outlined
                                    dense
                                    label="Pie de página"
                                    hide-details="auto"
                                    rows="2"
                                    class="mt-3"
                                ></v-textarea>

                                <v-textarea
                                    v-model="localFooterSmall"
                                    outlined
                                    dense
                                    label="Pie de página, letra pequeña"
                                    hide-details="auto"
                                    rows="2"
                                    class="mt-3"
                                ></v-textarea>
                            </v-col>

                            <!-- AI Modification -->
                            <v-col cols="12" md="8">
                                <h3 class="mb-3 text-subtitle-1 font-weight-bold">
                                    <v-icon small left>mdi-wand-magic-sparkles</v-icon>
                                    Personalizar Template con IA
                                </h3>
                                <v-alert type="info" text dense class="mb-3">
                                    Describe cómo quieres modificar tu factura. Por ejemplo: "Añade un color azul al encabezado" o "Cambia el tamaño de la tabla"
                                </v-alert>

                                <v-textarea
                                    v-model="modificationPrompt"
                                    outlined
                                    label="¿Qué quieres modificar?"
                                    placeholder="Ej: Quiero que el encabezado sea más grande y en color azul"
                                    rows="3"
                                    hide-details="auto"
                                    :disabled="isGenerating"
                                ></v-textarea>

                                <div class="mt-3 d-flex gap-2">
                                    <v-btn
                                        color="primary"
                                        class="flex-grow-1"
                                        :loading="isGenerating"
                                        :disabled="!modificationPrompt || isGenerating"
                                        @click="modifyTemplate"
                                    >
                                        <v-icon left>mdi-auto-fix</v-icon>
                                        Aplicar Modificación con IA
                                    </v-btn>

                                    <v-btn
                                        color="secondary"
                                        outlined
                                        :disabled="isGenerating"
                                        @click="resetToDefault"
                                    >
                                        <v-icon left>mdi-restore</v-icon>
                                        Restaurar por Defecto
                                    </v-btn>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Preview Section - Full Width -->
        <v-row class="mt-4">
            <v-col cols="12">
                <v-card elevation="2">
                    <v-card-title class="primary white--text d-flex">
                        <v-icon left color="white">mdi-eye</v-icon>
                        Vista Previa
                        <v-spacer></v-spacer>
                        <v-select
                            v-model="selectedSampleInvoice"
                            :items="sampleInvoiceTypes"
                            label="Ejemplo"
                            dense
                            outlined
                            hide-details
                            dark
                            class="mt-2"
                            style="max-width: 200px;"
                            @change="loadSampleInvoice"
                        ></v-select>
                    </v-card-title>

                    <v-card-text class="pa-0">
                        <div class="preview-container">
                            <div v-if="isGenerating" class="text-center pa-8">
                                <v-progress-circular
                                    indeterminate
                                    color="primary"
                                    size="64"
                                ></v-progress-circular>
                                <p class="mt-4 text-h6">Generando template personalizado...</p>
                            </div>

                            <div v-else-if="samplePageData" class="preview-wrapper">
                                <!-- A4 Page with exact dimensions like in PDF -->
                                <div class="a4-page">
                                    <DynamicInvoiceViewerComponent
                                        :key="previewKey"
                                        :page="pageWithTemplate"
                                        :num_page="currentPage"
                                        :total_pages="totalPages"
                                    />
                                </div>

                                <!-- Pagination controls if multiple pages -->
                                <div v-if="totalPages > 1" class="pagination-controls text-center pa-4">
                                    <v-btn
                                        icon
                                        :disabled="currentPage === 0"
                                        @click="currentPage--; loadSampleInvoice();"
                                    >
                                        <v-icon>mdi-chevron-left</v-icon>
                                    </v-btn>
                                    <span class="mx-4">Página {{ currentPage + 1 }} de {{ totalPages }}</span>
                                    <v-btn
                                        icon
                                        :disabled="currentPage === totalPages - 1"
                                        @click="currentPage++; loadSampleInvoice();"
                                    >
                                        <v-icon>mdi-chevron-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import DynamicInvoiceViewerComponent from "@/modules/facturae/infra/pdf_view/DynamicInvoiceViewerComponent.vue";
import { InvoiceTemplatePlaygroundComponent } from "@/modules/facturae/app/InvoiceTemplatePlaygroundComponent.ts";
import { InvoiceTemplateApiRepository } from "@/modules/facturae/infra/InvoiceTemplateApiRepository.ts";
import { DEFAULT_INVOICE_TEMPLATE } from "@/modules/facturae/infra/pdf_view/DefaultInvoiceTemplate.js";

export default {
    name: "CustomInvoiceTemplateEditor",
    components: {
        DynamicInvoiceViewerComponent
    },
    props: {
        configuration: {
            type: Object,
            required: true
        },
        companyId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            logoFile: null,
            localFooter: '',
            localFooterSmall: '',
            modificationPrompt: '',
            isGenerating: false,
            templateCode: DEFAULT_INVOICE_TEMPLATE,
            templateOptions: null,
            selectedSampleInvoice: 'basic',
            sampleInvoiceTypes: [
                { text: 'Básica', value: 'basic' },
                { text: 'Compleja', value: 'complex' }
            ],
            samplePageData: null,
            currentPage: 0,
            totalPages: 1,
            playgroundComponent: null,
            snackbar: {
                isVisible: false,
                text: '',
                color: 'success',
                timeout: 3000
            }
        };
    },
    computed: {
        pageWithTemplate() {
            if (!this.samplePageData) return null;
            return {
                ...this.samplePageData,
                templateCode: this.templateCode,
                options: this.templateOptions
            };
        },
        previewKey() {
            return `${this.selectedSampleInvoice}-${this.currentPage}`;
        }
    },
    mounted() {
        // Initialize playground component
        const apiRepository = new InvoiceTemplateApiRepository();
        this.playgroundComponent = new InvoiceTemplatePlaygroundComponent(apiRepository, this.companyId);

        // Load initial values from configuration
        this.localFooter = this.configuration.footer || '';
        this.localFooterSmall = this.configuration.footer_small || '';

        // Load saved template if exists, otherwise use default
        if (this.configuration.invoice_template && this.configuration.invoice_template.html) {
            this.templateCode = this.configuration.invoice_template.html;
            this.templateOptions = this.configuration.invoice_template.options || null;
        } else {
            this.templateCode = DEFAULT_INVOICE_TEMPLATE;
            this.templateOptions = null;
        }

        // Load initial sample
        this.loadSampleInvoice();
    },
    watch: {
        localFooter(newVal) {
            this.$emit('update:footer', newVal);
        },
        localFooterSmall(newVal) {
            this.$emit('update:footer_small', newVal);
        },
        configuration: {
            handler(newVal) {
                if (newVal) {
                    this.localFooter = newVal.footer || '';
                    this.localFooterSmall = newVal.footer_small || '';
                }
            },
            deep: true
        }
    },
    methods: {
        onLogoChange(file) {
            this.$emit('update:logo', file);
        },

        loadSampleInvoice() {
            const sampleData = this.playgroundComponent.getSampleInvoiceData(
                this.selectedSampleInvoice,
                this.currentPage
            );

            // Update configuration with current values
            sampleData.configuration.footer = this.localFooter;
            sampleData.configuration.footer_small = this.localFooterSmall;
            if (this.configuration.path_url) {
                sampleData.configuration.path_url = this.configuration.path_url;
            }

            this.samplePageData = { ...sampleData };
            this.totalPages = 1; // For now, single page preview
        },

        async modifyTemplate() {
            if (!this.modificationPrompt) return;

            this.isGenerating = true;
            try {
                const result = await this.playgroundComponent.modifyTemplate(
                    this.modificationPrompt,
                    this.templateCode,
                    this.templateOptions
                );

                this.templateCode = result.template;
                this.templateOptions = result.options;

                // Auto-save the template
                await this.saveTemplate();

                this.showSnackbar('Template modificado correctamente', 'success');
                this.modificationPrompt = ''; // Clear prompt after successful modification

                // Reload preview
                this.loadSampleInvoice();
            } catch (error) {
                console.error('Error modifying template:', error);
                this.showSnackbar('Error al modificar el template: ' + error.message, 'error');
            } finally {
                this.isGenerating = false;
            }
        },

        async resetToDefault() {
            if (!confirm('¿Estás seguro de que quieres restaurar el template por defecto? Se perderán todas las personalizaciones.')) {
                return;
            }

            this.templateCode = DEFAULT_INVOICE_TEMPLATE;
            this.templateOptions = null;

            // Save the default template
            try {
                await this.saveTemplate();
                this.showSnackbar('Template restaurado al diseño por defecto', 'success');
                this.loadSampleInvoice();
            } catch (error) {
                console.error('Error resetting template:', error);
                this.showSnackbar('Error al restaurar el template', 'error');
            }
        },

        async saveTemplate() {
            try {
                await this.playgroundComponent.saveTemplate({
                    name: 'Template personalizado',
                    template: this.templateCode,
                    options: this.templateOptions
                });

                // Emit event to parent to reload configuration
                this.$emit('template-saved');
            } catch (error) {
                console.error('Error saving template:', error);
                throw error;
            }
        },

        showSnackbar(text, color = 'success') {
            this.snackbar.text = text;
            this.snackbar.color = color;
            this.snackbar.isVisible = true;
        }
    }
};
</script>

<style scoped>
.preview-container {
    background-color: #e0e0e0;
    min-height: 80vh;
    overflow-y: auto;
    overflow-x: auto;
    padding: 32px 16px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
}

.preview-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* A4 dimensions: 210mm x 297mm */
/* Using actual A4 size for faithful representation */
.a4-page {
    width: 210mm;
    min-height: 297mm;
    background-color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin: 0 auto 24px auto;
    position: relative;
    overflow: hidden;
    padding: 15mm 12mm;
    box-sizing: border-box;
}

.pagination-controls {
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-top: 16px;
    padding: 8px 16px;
}

.gap-2 {
    gap: 8px;
}

@media screen and (max-width: 1200px) {
    .a4-page {
        width: 180mm;
        min-height: auto;
    }
}

@media screen and (max-width: 900px) {
    .a4-page {
        width: 100%;
        min-height: auto;
    }
}
</style>
